import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  TabList,
  Tab,
  Spinner,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  Briefcase20Regular,
  People20Regular,
  Folder20Regular,
  Mail20Regular,
  MoreHorizontalRegular,
  PeopleLock20Regular,
  LockClosed16Regular,
  Star20Regular,
  Star20Filled,
  Star16Filled,
  TabAddRegular,
  SearchRegular,
  FolderAdd20Regular,
  MailArrowUp20Regular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { FolderItemIcon, MailItemIcon } from '@/components/ItemIcon';
import { UnreadBadge } from '@/components/UnreadBadge';
import { MailReadMenuItem } from '@/components/MailReadMenuItem';
import { ItemActionMenuItems } from '@/components/ItemActionMenuItems';
import { useSearch, useResolvedScope } from '@/hooks/useSearch';
import { useUiStore, useSearchQuery, useSearchFilters } from '@/stores/uiStore';
import { hasActiveFilters, countActiveFilters } from '@/types/searchFilters';
import { useTreeStore } from '@/stores/treeStore';
import { useRecentProjectsStore } from '@/stores/recentProjectsStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { useContainers } from '@/hooks/useContainers';
import { useFavoriteIds, useToggleFavorite } from '@/hooks/useFavorites';
import { useItemClipboard } from '@/hooks/useItemClipboard';
import { useCreateFolder } from '@/hooks/useCreateFolder';
import { useRenameFolder, useDeleteFolder } from '@/hooks/useFolderMutations';
import { useRenameMail, useDeleteMail } from '@/hooks/useMailMutations';
import { useSetMailRead } from '@/hooks/useSetMailRead';
import { useFileMail } from '@/features/file-mail/useFileMail';
import { useFileMailToasts } from '@/features/multi-select/useFileMailToasts';
import { useRowSelection } from '@/features/multi-select/useRowSelection';
import { useMultiSelectionMenu } from '@/features/multi-select/useMultiSelectionMenu';
import { searchScope, type SelectedRef } from '@/features/multi-select/selection';
import { listFolders } from '@/services/api/endpoints';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { ContainerVisibilityDialog } from '@/features/visibility/ContainerVisibilityDialog';
import { CollaborationMenu } from '@/features/tree/CollaborationMenu';
import { MailTargetDialog, type MailTargetItem } from '@/features/tree/MailTargetDialog';
import { MoveFolderDialog, type FolderTargetItem } from '@/features/tree/MoveFolderDialog';
import { openMail } from '@/utils/openMail';
import { formatMailDate } from '@/utils/mailDate';
import { menuPositioning, pointTarget, type CursorTarget } from '@/components/cursorMenu';
import type { ClipboardFolder } from '@/stores/clipboardStore';
import type { Container, Folder, SearchHit } from '@/types/domain';

/**
 * Ouvre un espace ou un dossier en page détail DANS UN NOUVEL ONGLET.
 *
 * `folderId` à `null` = racine de l'espace. Seule façon d'ouvrir un résultat
 * depuis la recherche, au double-clic comme par le menu « Ouvrir dans un nouvel
 * onglet » : l'onglet d'origine doit rester sur ses résultats.
 *
 * Remplace deux helpers qui RÉUTILISAIENT un onglet existant du même espace
 * (`openContainerTab` / `openFolder`). Ce recyclage écrasait la page — et donc la
 * recherche — de l'onglet en question, exactement ce qu'on cherche à éviter.
 */
function openInNewTab(containerId: string, folderId: string | null): void {
  useTreeStore.getState().openDetailTab(containerId, folderId);
}

const useStyles = makeStyles({
  hit: {
    cursor: 'pointer',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    // C'est le DOUBLE-CLIC qui ouvre : sans ceci, chaque ouverture surlignerait
    // au passage le mot sous le curseur. Même parti que la page détail et que
    // l'Explorateur.
    userSelect: 'none',
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  /**
   * Curseur CLAVIER (flèches ↑/↓, et c'est de lui que part Entrée).
   *
   * UN LISERÉ, PLUS UN FOND, et ce n'est pas cosmétique : la sélection multiple
   * peint désormais les lignes retenues avec `colorBrandBackground2`, qui était
   * exactement la couleur de ce curseur. Les deux états auraient donc été
   * indiscernables — la première ligne aurait paru sélectionnée avant tout geste,
   * et un CTRL+clic qui DÉSÉLECTIONNE aurait laissé sa ligne allumée comme les
   * autres. Le liseré dit « Entrée part d'ici », le fond dit « ceci est retenu »,
   * et une ligne peut légitimement être les deux à la fois.
   */
  focused: {
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '-2px',
  },
  /**
   * Ligne RETENUE par la sélection multiple. Même teinte que le dossier actif de
   * l'arbre et que la ligne sélectionnée de la page détail.
   *
   * Le `:hover` est REDIT ICI volontairement : `hit` en pose un, et sans cette
   * reprise la sélection s'effacerait sous le curseur — c'est-à-dire à l'instant
   * même où l'utilisateur vient de cliquer, la souris étant encore sur la ligne.
   * `mergeClasses` place cette classe après `hit`, donc c'est elle qui l'emporte.
   * Même piège que celui documenté dans `FolderDetail`.
   */
  selected: {
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': { backgroundColor: tokens.colorBrandBackground2 },
  },
  hitBody: { flex: 1, minWidth: 0 },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Ici le libellé est DÉJÀ en demi-gras : seule la couleur le distingue du reste,
  // d'où la pastille (cf. components/UnreadBadge) qui porte l'essentiel.
  unreadLabel: { color: tokens.colorBrandForeground1 },
  snippet: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Highlighted match inside a result title (inspiration New Harmon.ie). Yellow
  // palette tokens stay legible over both the default row and the focused row's
  // brand background, and are theme-aware (unlike a hardcoded yellow).
  mark: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorNeutralForeground1,
    borderRadius: tokens.borderRadiusSmall,
  },
  section: { marginBottom: tokens.spacingVerticalS },
  // Section titles read as dividers, not clickable rows: no leading icon, small
  // uppercase grey text with a touch of tracking.
  sectionLabel: {
    textTransform: 'uppercase',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
  },
  // Count pill shared by the section headers and the filter tabs — replaces the
  // old "(N)" parenthesised counts.
  countBadge: {
    flexShrink: 0,
    marginInlineStart: tokens.spacingHorizontalXS,
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground3,
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
  },
  scopeHint: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // États vides de la recherche : bloc centré, respirant, avec une SORTIE
  // cliquable. Volontairement plus généreux que l'état vide d'un espace dans
  // l'arbre : ici la recherche occupe tout le volet, un texte collé en haut à
  // gauche laisse l'écran vide et ressemble à une erreur.
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: { fontSize: '28px', color: tokens.colorNeutralForeground4 },
  emptyTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    // Un terme de recherche très long ne doit pas élargir le volet.
    overflowWrap: 'anywhere',
  },
  emptyHint: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: tokens.spacingVerticalXS,
    overflowWrap: 'anywhere',
  },
  emptyKeyHint: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground4 },
  favStar: { flexShrink: 0, color: '#eaa300' },
  // Cadenas d'un espace à visibilité restreinte. Reste NEUTRE : dans ce projet
  // seules les icônes d'ÉLÉMENT (dossier/mail) et l'étoile favori sont colorées,
  // un indicateur d'état ne doit pas concurrencer le titre du résultat.
  restrictedIcon: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  // Déclencheur ⋯ révélé au survol — même correctif que dans `ContainerSection` :
  // `display: none` le sortait de l'ordre de tabulation, donc les actions du menu
  // (ouvrir dans un onglet, favori, visibilité…) étaient inatteignables au
  // clavier. `opacity: 0` garde l'élément focusable, `:focus-visible` /
  // `:focus-within` le réaffichent dès qu'il prend le focus, et
  // `pointerEvents: 'none'` à l'état masqué évite un bouton invisible qui
  // volerait les clics de la ligne.
  hoverOnly: {
    opacity: 0,
    pointerEvents: 'none',
    ':focus-visible': { opacity: 1, pointerEvents: 'auto' },
    ':focus-within': { opacity: 1, pointerEvents: 'auto' },
  },
  hoverShown: {},
  // Enveloppe du menu ⋯ : voir le commentaire de son usage dans HitRow (portails
  // Fluent et remontée des événements dans l'arbre React). `flex` + `flexShrink`
  // pour que le bouton reste dimensionné comme avant l'ajout du wrapper.
  menuWrap: { display: 'flex', flexShrink: 0 },
});

// Small neutral count pill. Used both in the filter tabs and in the section
// headers so the two places stay visually consistent.
function CountBadge({ count }: { count: number }) {
  const styles = useStyles();
  return (
    <Badge
      className={styles.countBadge}
      appearance="tint"
      color="informative"
      size="small"
      shape="circular"
    >
      {count}
    </Badge>
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Highlight every occurrence of the query terms inside a result title, the way
// New Harmon.ie does. Splitting on a single capturing alternation keeps the
// matched fragments in the array; a fragment is a match when it equals one of
// the (lower-cased) tokens. Purely presentational — no HTML injection.
function HighlightText({ text, query }: { text: string; query: string }) {
  const styles = useStyles();
  const tokens_ = query.trim().split(/\s+/).filter((t) => t.length > 0);
  if (tokens_.length === 0) return <>{text}</>;
  const lower = new Set(tokens_.map((t) => t.toLowerCase()));
  const re = new RegExp(`(${tokens_.map(escapeRegExp).join('|')})`, 'gi');
  const parts = text.split(re).filter((p) => p !== '');
  return (
    <>
      {parts.map((part, i) =>
        lower.has(part.toLowerCase()) ? (
          <mark key={i} className={styles.mark}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

type FilterKind = 'all' | 'project' | 'team_space' | 'folder' | 'mail';

/**
 * TOUT ce qui s'ouvre par-dessus la liste, dans UN état.
 *
 * Deux raisons de n'avoir qu'une variable plutôt qu'un booléen par dialogue :
 *
 * 1. Le listener clavier de cette page est posé sur `window` (flèches + Entrée
 *    pour parcourir les résultats). Il doit être SUSPENDU dès qu'un dialogue est
 *    ouvert, sinon Entrée valide le dialogue *et* active le résultat surligné
 *    derrière — donc ouvre un onglet et démonte le dialogue en cours
 *    d'utilisation. Le piège avait déjà été rencontré avec la popup de
 *    visibilité, seule modale de l'époque ; avec huit dialogues, une garde par
 *    dialogue finirait immanquablement par en oublier un. Ici la garde est
 *    `modal !== null` : l'oubli devient impossible.
 * 2. Deux dialogues ne peuvent pas s'ouvrir en même temps, ce que le type dit.
 */
type Modal =
  | { kind: 'visibility'; container: Container }
  | { kind: 'createFolder'; containerId: string; parentId: string | null; title: string }
  | { kind: 'renameFolder'; folderId: string }
  | { kind: 'deleteFolder'; folderId: string; name: string }
  | { kind: 'folderTarget'; mode: 'copy' | 'move'; folder: FolderTargetItem }
  | { kind: 'renameMail'; mailId: string; folderId: string | null }
  | { kind: 'deleteMail'; mailId: string; folderId: string | null; label: string }
  | { kind: 'mailTarget'; mode: 'copy' | 'move'; mail: MailTargetItem };

interface FlatHit {
  hit: SearchHit;
  kind: 'container' | 'folder' | 'mail';
  activate: () => void;
}

/**
 * Les surfaces flottantes de Fluent, pour que le clavier de la LISTE les ignore.
 *
 * Le listener de cette page est posé sur `window` : une touche frappée dans un
 * menu contextuel ou dans un dialogue lui parvient donc aussi. Entrée validerait
 * l'entrée de menu **et** ouvrirait la ligne surlignée derrière ; les flèches
 * déplaceraient le curseur de la liste pendant qu'on parcourt un menu.
 *
 * L'état `modal` ci-dessous garde déjà les dialogues montés PAR CETTE LISTE ;
 * cette garde-ci couvre ceux que la liste ne connaît pas — les menus par ligne et
 * les dialogues du menu de LOT, qui vivent dans `HitRow`. Les deux se cumulent.
 */
const OVERLAY_SELECTOR = '[role="dialog"], [role="menu"], [role="listbox"]';

// Greyed "path" subtitle under a hit title: project · parent-folder [· date].
// Names come from fn_search `extra` (they aren't resolvable client-side because
// search spans containers whose folders aren't loaded). Returns '' when nothing
// meaningful is available (e.g. container hits, or before 0018 is deployed).
function buildSubtitle(f: FlatHit): string {
  if (f.kind === 'container') return '';
  const ex = f.hit.extra ?? {};
  const parts: string[] = [];
  if (ex.containerName) parts.push(ex.containerName);
  if (f.kind === 'folder' && ex.parentName) parts.push(ex.parentName);
  if (f.kind === 'mail' && ex.folderName) parts.push(ex.folderName);
  if (f.kind === 'mail') {
    const d = formatMailDate(ex.receivedAt ?? null);
    if (d) parts.push(d);
  }
  return parts.join(' · ');
}

export function SearchResults() {
  const styles = useStyles();
  const query = useSearchQuery();
  const searchFilters = useSearchFilters();
  const filtersActive = hasActiveFilters(searchFilters);
  const activeFilterCount = countActiveFilters(searchFilters);
  const clearSearchFilters = useUiStore((s) => s.clearSearchFilters);
  const setSearchQuery = useUiStore((s) => s.setSearchQuery);
  const touchRecent = useRecentProjectsStore((s) => s.touch);
  const { data, isPending, isFetching } = useSearch();
  const { data: containers } = useContainers();
  const favIds = useFavoriteIds();
  const toggleFavorite = useToggleFavorite();
  const [focusedIdx, setFocusedIdx] = useState(0);
  const [filter, setFilter] = useState<FilterKind>('all');
  // Le dialogue ouvert, s'il y en a un. L'état vit ICI, pas dans HitRow : une
  // recherche affiche des dizaines de lignes, et monter les dialogues par ligne
  // multiplierait les portails (et leurs requêtes) pour rien. Une seule instance
  // de chacun, pour la ligne visée. Cf. le commentaire du type `Modal`.
  const [modal, setModal] = useState<Modal | null>(null);
  // Champ de saisie partagé par les dialogues « Nouveau (sous-)dossier » et
  // « Renommer » : un seul est ouvert à la fois (cf. `Modal`).
  const [nameValue, setNameValue] = useState('');

  // Actions des menus contextuels. Ce sont exactement les hooks de l'arbre et de
  // la page détail : les trois surfaces partagent donc le même comportement, y
  // compris les toasts de refus du serveur.
  const qc = useQueryClient();
  const selection = useSelectionStore((s) => s.items);
  const fileMailMut = useFileMail();
  const { showLoading, showResults, showError } = useFileMailToasts();
  const { canPaste, pending, isBusy, copyMails, cutMails, copyFolder, cutFolder, pasteInto } =
    useItemClipboard();
  const createFolderMut = useCreateFolder();
  const renameFolderMut = useRenameFolder();
  const deleteFolderMut = useDeleteFolder();
  const renameMailMut = useRenameMail();
  const deleteMailMut = useDeleteMail();
  const setRead = useSetMailRead();

  // Infobulle de « Coller » : dire CE QUI sera collé, le presse-papiers étant
  // partagé entre mails et dossiers. Même texte que dans l'arbre.
  const pasteHint =
    pending === 'folder'
      ? 'Coller le dossier copié dans ce dossier'
      : pending === 'mails'
        ? 'Coller les mails copiés dans ce dossier'
        : undefined;

  const closeModal = () => {
    setModal(null);
    setNameValue('');
  };

  /**
   * Range la sélection Outlook dans un dossier PRÉCIS — même code que l'arbre.
   *
   * `containerId` est EXIGÉ, et c'est ce chemin qui l'imposait : un dossier issu
   * des résultats de recherche peut appartenir à un espace dont l'arborescence n'a
   * jamais été chargée, donc son projet ne se retrouve pas dans les caches. Le hit
   * le porte (`hit.containerId`), il suffit de le transmettre.
   */
  const fileSelectionInto = (folderId: string, containerId: string) => {
    if (selection.length === 0) return;
    const toastId = showLoading(selection.length);
    fileMailMut.mutate(
      { targetFolderId: folderId, targetContainerId: containerId, items: selection },
      {
        onSuccess: (results) => showResults(results, toastId),
        onError: (err) => showError(err instanceof Error ? err.message : String(err), toastId),
      },
    );
  };

  /**
   * « Ranger ici » sur un ESPACE : il faut d'abord un dossier, un espace n'en
   * étant pas un.
   *
   * L'arbre applique l'heuristique « s'il n'existe qu'un seul dossier racine, ce
   * sera celui-là », et à défaut déplie la section pour laisser choisir. Ce
   * dépliement n'a pas d'équivalent dans une liste plate de résultats : le repli
   * est donc d'OUVRIR l'espace dans un onglet, où le menu de fond propose
   * « Ranger N mail(s) ici » sur le dossier réellement affiché.
   *
   * L'arborescence est lue par `fetchQuery` (même clé et même `staleTime` que
   * `useFolders`) : si l'utilisateur a déjà ouvert cet espace, c'est gratuit.
   */
  const fileSelectionIntoContainer = async (containerId: string) => {
    if (selection.length === 0) return;
    try {
      const folders = await qc.fetchQuery<Folder[]>({
        queryKey: ['folders', containerId],
        queryFn: () => listFolders(containerId),
        staleTime: 30_000,
      });
      const roots = folders.filter((f) => !f.parentId);
      if (roots.length === 1 && roots[0]) {
        fileSelectionInto(roots[0].id, containerId);
        return;
      }
    } catch {
      // Arborescence illisible (réseau, droits) : on ne devine pas de cible, on
      // ouvre l'espace pour que le choix soit explicite.
    }
    touchRecent(containerId);
    openInNewTab(containerId, null);
  };

  const openCreateFolder = (containerId: string, parentId: string | null, title: string) => {
    setNameValue('');
    setModal({ kind: 'createFolder', containerId, parentId, title });
  };

  const submitCreateFolder = () => {
    if (modal?.kind !== 'createFolder') return;
    const name = nameValue.trim();
    if (!name) return;
    createFolderMut.mutate(
      { containerId: modal.containerId, parentId: modal.parentId, name },
      // Fermeture sur SUCCÈS seulement : un nom refusé (doublon) laisse le
      // dialogue ouvert pour corriger sur place, la raison arrivant en toast
      // depuis useCreateFolder. Même règle que la page détail.
      { onSuccess: closeModal },
    );
  };

  const submitRenameFolder = () => {
    if (modal?.kind !== 'renameFolder') return;
    const name = nameValue.trim();
    if (!name) {
      closeModal();
      return;
    }
    renameFolderMut.mutate({ folderId: modal.folderId, name }, { onSettled: closeModal });
  };

  const submitRenameMail = () => {
    if (modal?.kind !== 'renameMail') return;
    // Un nom vide REVIENT à l'objet d'origine (c'est ce que dit le placeholder) :
    // on ne l'écarte donc pas comme pour un dossier.
    renameMailMut.mutate(
      { mailId: modal.mailId, folderId: modal.folderId, displayName: nameValue.trim() },
      { onSettled: closeModal },
    );
  };

  const submitDeleteFolder = () => {
    if (modal?.kind !== 'deleteFolder') return;
    deleteFolderMut.mutate({ folderId: modal.folderId }, { onSettled: closeModal });
  };

  const submitDeleteMail = () => {
    if (modal?.kind !== 'deleteMail') return;
    deleteMailMut.mutate(
      { mailId: modal.mailId, folderId: modal.folderId },
      { onSettled: closeModal },
    );
  };

  /**
   * Entrée de presse-papiers pour un dossier trouvé par la recherche.
   *
   * `fn_search` ne renvoie PAS le parent d'un dossier (seulement son nom, pour le
   * sous-titre). S'il se trouve que l'arborescence de cet espace est déjà en cache
   * — l'utilisateur l'a ouverte — on y lit le parent ; sinon on l'omet, ce qui
   * signifie « inconnu » et non « racine » (cf. `ClipboardFolder.parentId`). Le
   * seul effet est de sauter le raccourci « couper-coller sur place », que le
   * serveur reconnaît de son côté.
   */
  const clipboardFolderOf = (hit: SearchHit, containerId: string): ClipboardFolder => {
    const cached = qc.getQueryData<Folder[]>(['folders', containerId]);
    const parentId = cached?.find((f) => f.id === hit.id)?.parentId;
    return {
      folderId: hit.id,
      containerId,
      name: hit.label,
      ...(parentId !== undefined ? { parentId } : {}),
    };
  };

  /**
   * Ce qu'un résultat représente pour la MULTI-SÉLECTION — ou `null` s'il n'y
   * participe pas.
   *
   * DEUX RÉSULTATS SUR TROIS SEULEMENT. Un ESPACE n'est pas sélectionnable : le
   * modèle partagé ne connaît que `mail` et `folder` (cf. `SelectedKind`), et
   * c'est juste — aucune des actions de lot (déplacer, supprimer, marquer lu) ne
   * veut dire quoi que ce soit sur un projet. Un dossier ou un mail dont le hit
   * ne porte pas de conteneur est écarté pour la même raison que son menu :
   * rien ne serait adressable.
   *
   * `parentId` d'un dossier : `fn_search` ne le renvoie pas. On le lit dans
   * l'arborescence si elle est déjà en cache, sinon `null`. La conséquence est
   * bornée — `pruneRedundant` ne saura pas qu'un dossier sélectionné emporte
   * déjà celui-ci, et il fera l'objet d'un appel propre. Or ce cas suppose qu'un
   * dossier ET son descendant répondent tous deux à la recherche, et que
   * l'espace n'ait jamais été ouvert. Même compromis, et même raison, que
   * `clipboardFolderOf` juste au-dessus.
   */
  const refOfHit = (f: FlatHit): SelectedRef | null => {
    const containerId = f.hit.containerId;
    if (f.kind === 'container' || !containerId) return null;
    if (f.kind === 'mail') {
      return {
        kind: 'mail',
        id: f.hit.id,
        containerId,
        // `null` = mail rangé à la RACINE de l'espace, ce que le modèle attend.
        parentId: f.hit.folderId ?? null,
        label: f.hit.label,
      };
    }
    const cached = qc.getQueryData<Folder[]>(['folders', containerId]);
    return {
      kind: 'folder',
      id: f.hit.id,
      containerId,
      parentId: cached?.find((x) => x.id === f.hit.id)?.parentId ?? null,
      label: f.hit.label,
    };
  };

  // --- Les trois menus contextuels ----------------------------------------
  //
  // MÊME LISTE D'ACTIONS QUE L'ARBRE ET LA PAGE DÉTAIL. La fin des menus dossier
  // et mail vient de `ItemActionMenuItems`, le composant partagé : libellés,
  // icônes, ordre et séparateurs ne peuvent donc plus dériver d'une surface à
  // l'autre. Seule la TÊTE — ce qui est propre à la nature de l'élément — est
  // écrite ici, comme dans `FolderRow`, `MailRow` et `FolderDetail`.

  const containerMenu = (hit: SearchHit, container: Container | undefined, isFavorite: boolean) => (
    <>
      {selection.length > 0 && (
        <>
          <MenuItem
            icon={<MailArrowUp20Regular />}
            onClick={() => void fileSelectionIntoContainer(hit.id)}
          >
            Ranger {selection.length} mail(s) ici
          </MenuItem>
          <MenuDivider />
        </>
      )}
      <MenuItem
        icon={<TabAddRegular />}
        onClick={() => {
          touchRecent(hit.id);
          openInNewTab(hit.id, null);
        }}
      >
        Ouvrir dans un nouvel onglet
      </MenuItem>
      <MenuItem
        icon={<FolderAdd20Regular />}
        onClick={() => openCreateFolder(hit.id, null, 'Nouveau dossier')}
      >
        Nouveau dossier
      </MenuItem>
      {/* Le sous-menu et la visibilité exigent le Container COMPLET (prismId,
          m365GroupId, kind) : on ne les propose que si useContainers() a résolu ce
          hit. Sinon la liste n'est pas encore chargée. */}
      {container && (
        <>
          <MenuDivider />
          <CollaborationMenu container={container} />
        </>
      )}
      <MenuDivider />
      <MenuItem
        icon={isFavorite ? <Star20Filled /> : <Star20Regular />}
        onClick={() => toggleFavorite.mutate({ containerId: hit.id, favorite: !isFavorite })}
      >
        {isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      </MenuItem>
      {container && (
        <MenuItem icon={<PeopleLock20Regular />} onClick={() => setModal({ kind: 'visibility', container })}>
          Gérer la visibilité
        </MenuItem>
      )}
    </>
  );

  const folderMenu = (hit: SearchHit): ReactNode => {
    // Sans conteneur, aucune de ces actions n'est adressable. `fn_search` le
    // renvoie toujours pour un dossier ; la garde évite un menu à moitié mort.
    const containerId = hit.containerId;
    if (!containerId) return null;
    const folderId = hit.id;
    return (
      <>
        {selection.length > 0 && (
          <>
            <MenuItem
              icon={<MailArrowUp20Regular />}
              onClick={() => fileSelectionInto(folderId, containerId)}
            >
              Ranger {selection.length} mail(s) ici
            </MenuItem>
            <MenuDivider />
          </>
        )}
        {/* Pas d'« Ouvrir » dans l'onglet courant, contrairement à la page détail :
            la recherche occupe tout le volet, et réutiliser cet onglet effacerait
            la liste de résultats — la régression que corrige `openInNewTab`. */}
        <MenuItem icon={<TabAddRegular />} onClick={() => openInNewTab(containerId, folderId)}>
          Ouvrir dans un nouvel onglet
        </MenuItem>
        <MenuItem
          icon={<FolderAdd20Regular />}
          onClick={() => openCreateFolder(containerId, folderId, 'Nouveau sous-dossier')}
        >
          Nouveau sous-dossier
        </MenuItem>
        <ItemActionMenuItems
          onRename={() => {
            setNameValue(hit.label);
            setModal({ kind: 'renameFolder', folderId });
          }}
          onCopy={() => copyFolder(clipboardFolderOf(hit, containerId))}
          onCut={() => cutFolder(clipboardFolderOf(hit, containerId))}
          onPaste={() => pasteInto(folderId, containerId)}
          pasteDisabled={!canPaste || isBusy}
          pasteHint={pasteHint}
          onCopyTo={() =>
            setModal({
              kind: 'folderTarget',
              mode: 'copy',
              folder: { id: folderId, name: hit.label, containerId },
            })
          }
          onMoveTo={() =>
            setModal({
              kind: 'folderTarget',
              mode: 'move',
              folder: { id: folderId, name: hit.label, containerId },
            })
          }
          onDelete={() => setModal({ kind: 'deleteFolder', folderId, name: hit.label })}
        />
      </>
    );
  };

  const mailMenu = (hit: SearchHit): ReactNode => {
    const containerId = hit.containerId;
    if (!containerId) return null;
    const mailId = hit.id;
    // `null` = mail rangé à la RACINE d'un projet. Ni « Coller » ni un collage
    // n'ont alors de dossier cible.
    const folderId = hit.folderId ?? null;
    return (
      <>
        <MenuItem
          icon={<ArrowDownloadRegular />}
          onClick={() => {
            // Ce chemin compte AUSSI comme une ouverture : c'est le même geste que
            // le double-clic sur la ligne, par un autre bouton.
            setRead(mailId, true);
            void openMail(mailId, hit.label);
          }}
        >
          Ouvrir / Télécharger
        </MenuItem>
        {/* Présente ICI AUSSI, alors que la demande ne visait que l'accueil et la
            page détail : ces résultats affichent la MÊME pastille, et une pastille
            qu'on ne peut pas éteindre là où on la voit serait une impasse. */}
        <MailReadMenuItem mailId={mailId} isRead={hit.isRead} />
        <ItemActionMenuItems
          onRename={() => {
            // `hit.label` fusionne display_name, subject et le repli
            // « (sans objet) » — pré-remplir ce repli le ferait ENREGISTRER comme
            // nom d'affichage. Champ vide dans ce cas, ce qui signifie
            // « objet d'origine ».
            setNameValue(hit.label === '(sans objet)' ? '' : hit.label);
            setModal({ kind: 'renameMail', mailId, folderId });
          }}
          onCopy={() => copyMails([{ mailId, folderId, containerId, label: hit.label }])}
          onCut={() => cutMails([{ mailId, folderId, containerId, label: hit.label }])}
          onPaste={() => {
            if (folderId) pasteInto(folderId, containerId);
          }}
          pasteDisabled={!canPaste || isBusy || folderId === null}
          pasteHint={pasteHint}
          onCopyTo={() =>
            setModal({
              kind: 'mailTarget',
              mode: 'copy',
              mail: { id: mailId, folderId, containerId, displayName: hit.label },
            })
          }
          onMoveTo={() =>
            setModal({
              kind: 'mailTarget',
              mode: 'move',
              mail: { id: mailId, folderId, containerId, displayName: hit.label },
            })
          }
          onDelete={() => setModal({ kind: 'deleteMail', mailId, folderId, label: hit.label })}
        />
      </>
    );
  };

  // Portée effective réellement interrogée (défaut contextuel ou choix manuel,
  // avec repli sur 'all' si le projet/dossier cible est indisponible), pour que le
  // hint et les onglets de filtre restent honnêtes sur ce qui a été cherché.
  const resolvedScope = useResolvedScope();
  const effectiveScope = resolvedScope.scope;
  // Drilled into one project/folder → the "Projets/Espaces" facets are always
  // empty, so hide them to avoid noisy "(0)" tabs.
  const showContainerFacets = effectiveScope === 'all' || effectiveScope === 'favorites';

  const containerById = useMemo(
    () => new Map((containers ?? []).map((c) => [c.id, c] as const)),
    [containers],
  );

  // OUVERTURE D'UN RÉSULTAT : DANS UN NOUVEL ONGLET, en mode page détail. Et
  // l'onglet d'où l'on vient garde ses résultats intacts.
  //
  // AU DOUBLE-CLIC (ou Entrée), plus au clic simple, désormais réservé à la
  // sélection : c'est la convention de l'arbre, de la page détail et de
  // l'Explorateur. Viser une ligne pour la désigner — la comparer à une autre,
  // ouvrir son menu, en cocher plusieurs — ne doit pas ouvrir un onglet ni lancer
  // le téléchargement d'un .msg.
  //
  // Avant, un clic réutilisait l'onglet courant (`openContainerTab` / `openFolder`)
  // PUIS vidait la recherche : on perdait la liste de résultats, et revenir
  // dessus imposait de tout retaper. Deux choses rendent le comportement actuel
  // possible : `openDetailTab` ouvre directement la page détail dans un onglet
  // neuf, et le terme de recherche est désormais porté PAR ONGLET (cf. uiStore) —
  // sans quoi le nouvel onglet afficherait à son tour la liste de résultats au
  // lieu de la page demandée.
  const allFlat: FlatHit[] = useMemo(() => {
    const out: FlatHit[] = [];
    for (const hit of data?.containers ?? []) {
      out.push({
        hit,
        kind: 'container',
        activate: () => {
          addBreadcrumb({ category: 'ui.click', message: 'search-container', data: { id: hit.id } });
          touchRecent(hit.id);
          // `null` = racine du projet (ses dossiers + ses mails de racine).
          openInNewTab(hit.id, null);
        },
      });
    }
    for (const hit of data?.folders ?? []) {
      out.push({
        hit,
        kind: 'folder',
        activate: () => {
          if (hit.containerId) {
            addBreadcrumb({ category: 'ui.click', message: 'search-folder', data: { id: hit.id } });
            touchRecent(hit.containerId);
            openInNewTab(hit.containerId, hit.id);
          }
        },
      });
    }
    for (const hit of data?.mails ?? []) {
      out.push({
        hit,
        kind: 'mail',
        activate: () => {
          // Download/open the .msg, same as double-clicking the mail in the
          // detail page. Search stays open so several mails can be grabbed in a row.
          addBreadcrumb({ category: 'ui.click', message: 'search-mail', data: { id: hit.id } });
          if (hit.containerId) touchRecent(hit.containerId);
          setRead(hit.id, true);
          void openMail(hit.id, hit.label);
        },
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const flat: FlatHit[] = useMemo(() => {
    if (filter === 'all') return allFlat;
    if (filter === 'project')
      return allFlat.filter((f) => f.kind === 'container' && f.hit.kind === 'project');
    if (filter === 'team_space')
      return allFlat.filter((f) => f.kind === 'container' && f.hit.kind === 'team_space');
    if (filter === 'folder') return allFlat.filter((f) => f.kind === 'folder');
    return allFlat.filter((f) => f.kind === 'mail');
  }, [allFlat, filter]);

  // Portée de la multi-sélection. Elle intègre le terme ET l'onglet de filtre :
  // c'est ce qui périme la sélection dès que la liste change d'identité, sans
  // écrire dans le store pendant un rendu (cf. `searchScope`).
  const scope = searchScope(query, filter);

  // Rang de chaque ligne dans la liste plate, pour que le CLIC déplace aussi le
  // curseur clavier. Sans cela, Entrée repartirait de la ligne surlignée au
  // chargement (la première) et non de celle que l'utilisateur vient de viser —
  // deux lignes marquées à l'écran pour deux notions différentes.
  const indexById = useMemo(
    () => new Map(flat.map((f, i) => [f.hit.id, i] as const)),
    [flat],
  );

  const projectCount = allFlat.filter(
    (f) => f.kind === 'container' && f.hit.kind === 'project',
  ).length;
  const teamCount = allFlat.filter(
    (f) => f.kind === 'container' && f.hit.kind === 'team_space',
  ).length;
  const folderCount = allFlat.filter((f) => f.kind === 'folder').length;
  const mailCount = allFlat.filter((f) => f.kind === 'mail').length;

  // DEUX CORRECTIONS D'ÉTAT PENDANT LE RENDU, et non dans un effet.
  //
  // C'est le motif que React documente pour « ajuster un état quand une prop
  // change » : le composant est immédiatement réexécuté avec la valeur corrigée,
  // AVANT la peinture. Les effets d'avant s'exécutaient après, donc une image
  // entière montrait l'ancien index appliqué à la nouvelle liste — un surlignage
  // sur la mauvaise ligne, le temps d'un battement. Et `setState` synchrone dans
  // un effet est précisément ce que react-hooks/set-state-in-effect signale.
  //
  // Chaque correction compare la valeur DÉJÀ VUE à la valeur courante ; sans ce
  // témoin, la remise à zéro se rejouerait à chaque rendu et l'utilisateur ne
  // pourrait plus déplacer le surlignage au clavier.

  // Le changement de portée peut rendre le filtre de catégorie inatteignable
  // (« Projets » alors qu'on ne cherche plus que dans un espace) : retour à
  // « Tous ». Traité AVANT le surlignage, parce qu'il change `filter`, dont
  // dépend le surlignage — les deux se règlent ainsi dans la même passe.
  const [seenScope, setSeenScope] = useState(effectiveScope);
  if (seenScope !== effectiveScope) {
    setSeenScope(effectiveScope);
    setFilter('all');
  }

  // Le surlignage clavier repart en tête dès que la liste change d'identité :
  // nouveaux résultats, ou changement de filtre.
  const [seenList, setSeenList] = useState<{ data: unknown; filter: FilterKind }>({ data, filter });
  if (seenList.data !== data || seenList.filter !== filter) {
    setSeenList({ data, filter });
    setFocusedIdx(0);
  }

  // Capture arrow + enter on the whole window so the SearchBox doesn't have
  // to forward keys.
  //
  // Suspendu tant QU'UN dialogue est ouvert, quel qu'il soit : ce listener est
  // posé sur `window`, il se déclencherait donc AUSSI depuis l'intérieur d'une
  // modale — Entrée sur un bouton du dialogue activerait en plus le résultat
  // surligné derrière (ce qui ouvre un onglet et démonte le dialogue en cours
  // d'utilisation), et les flèches perturberaient ses listes. La garde porte sur
  // `modal`, pas sur un dialogue nommé : ajouter un dialogue ne peut plus
  // réintroduire le bug.
  useEffect(() => {
    if (!query.trim() || flat.length === 0 || modal) return;
    const onKey = (e: KeyboardEvent) => {
      // Frappe DANS un menu ou un dialogue : elle ne concerne pas la liste
      // derrière (cf. OVERLAY_SELECTOR). Complète la garde `modal`, qui ne
      // connaît que les dialogues montés ici.
      if (e.target instanceof Element && e.target.closest(OVERLAY_SELECTOR)) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(i + 1, flat.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const item = flat[focusedIdx];
        if (item) item.activate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [query, flat, focusedIdx, modal]);

  // La recherche s'affiche dès qu'il y a un TERME **ou** un FILTRE : « les mails
  // de X en juin » n'a pas de mot-clé, et sans cette condition le volet resterait
  // sur l'arbre alors que la requête a bien été lancée.
  if (!query.trim() && !filtersActive) return null;
  if (isPending && isFetching) {
    return (
      <div className={styles.loading}>
        <Spinner size="small" label="Recherche en cours…" labelPosition="below" />
      </div>
    );
  }

  const filterBar = (
    <TabList
      selectedValue={filter}
      onTabSelect={(_e, d) => setFilter(d.value as FilterKind)}
      size="small"
      className={styles.filters}
    >
      <Tab value="all">
        Tous
        <CountBadge count={allFlat.length} />
      </Tab>
      {showContainerFacets && (
        <>
          <Tab value="project" icon={<Briefcase20Regular />}>
            Projets
            <CountBadge count={projectCount} />
          </Tab>
          <Tab value="team_space" icon={<People20Regular />}>
            Espaces
            <CountBadge count={teamCount} />
          </Tab>
        </>
      )}
      <Tab value="folder" icon={<Folder20Regular />}>
        Dossiers
        <CountBadge count={folderCount} />
      </Tab>
      <Tab value="mail" icon={<Mail20Regular />}>
        Mails
        <CountBadge count={mailCount} />
      </Tab>
    </TabList>
  );

  const SCOPE_HINT: Record<'container' | 'folder' | 'favorites', string> = {
    container: 'ce projet',
    folder: 'ce dossier et ses sous-dossiers',
    favorites: 'mes favoris',
  };
  // Pour la portée « projet », on rappelle le nom réel du projet plutôt que le
  // générique « ce projet ». resolvedScope.containerId est l'id ciblé par la
  // portée ; repli sur « ce projet » tant que les containers ne sont pas chargés
  // (ou id introuvable).
  const scopeName =
    effectiveScope === 'container' && resolvedScope.containerId
      ? containerById.get(resolvedScope.containerId)?.name
      : undefined;
  const scopeHint =
    effectiveScope === 'all' ? null : (
      <div className={styles.scopeHint}>
        Résultats limités à <strong>{scopeName ?? SCOPE_HINT[effectiveScope]}</strong>.
      </div>
    );

  // Instance UNIQUE de chaque dialogue pour toute la liste (cf. `modal`).
  //
  // Ce bloc est rendu dans CHAQUE branche de sortie ci-dessous, et ce n'est pas
  // une précaution de style : toutes ces actions invalident `['search']`, donc la
  // liste se recharge pendant que le dialogue est ouvert. Si l'élément visé sort
  // alors des résultats (renommé, supprimé, devenu invisible), c'est une autre
  // branche qui s'affiche — elle démonterait le dialogue en pleine action si elle
  // ne le portait pas aussi. Le piège avait été rencontré avec la popup de
  // visibilité, seule modale de l'époque.
  //
  // Les libellés sont recopiés à l'identique de l'arbre et de la page détail :
  // même geste, même phrase.
  const dialogs = (
    <>
      {modal?.kind === 'visibility' && (
        <ContainerVisibilityDialog
          container={modal.container}
          open
          onOpenChange={(o) => {
            if (!o) closeModal();
          }}
        />
      )}

      {modal?.kind === 'folderTarget' && (
        <MoveFolderDialog open mode={modal.mode} folder={modal.folder} onClose={closeModal} />
      )}

      {modal?.kind === 'mailTarget' && (
        <MailTargetDialog mode={modal.mode} mail={modal.mail} onClose={closeModal} />
      )}

      {modal?.kind === 'createFolder' && (
        <Dialog open onOpenChange={(_e, d) => !d.open && closeModal()}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{modal.title}</DialogTitle>
              <DialogContent>
                <Input
                  value={nameValue}
                  onChange={(_e, d) => setNameValue(d.value)}
                  placeholder="Nom du dossier"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && submitCreateFolder()}
                />
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeModal}>
                  Annuler
                </Button>
                <Button
                  appearance="primary"
                  disabled={!nameValue.trim() || createFolderMut.isPending}
                  onClick={submitCreateFolder}
                >
                  Créer
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {modal?.kind === 'renameFolder' && (
        <Dialog open onOpenChange={(_e, d) => !d.open && closeModal()}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Renommer le dossier</DialogTitle>
              <DialogContent>
                <Input
                  value={nameValue}
                  onChange={(_e, d) => setNameValue(d.value)}
                  placeholder="Nom du dossier"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && submitRenameFolder()}
                />
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeModal}>
                  Annuler
                </Button>
                <Button
                  appearance="primary"
                  disabled={renameFolderMut.isPending}
                  onClick={submitRenameFolder}
                >
                  Renommer
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {modal?.kind === 'renameMail' && (
        <Dialog open onOpenChange={(_e, d) => !d.open && closeModal()}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Renommer le mail</DialogTitle>
              <DialogContent>
                <Input
                  value={nameValue}
                  onChange={(_e, d) => setNameValue(d.value)}
                  placeholder="Nom d'affichage (vide = objet d'origine)"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && submitRenameMail()}
                />
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeModal}>
                  Annuler
                </Button>
                <Button
                  appearance="primary"
                  disabled={renameMailMut.isPending}
                  onClick={submitRenameMail}
                >
                  Renommer
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {modal?.kind === 'deleteFolder' && (
        <Dialog open onOpenChange={(_e, d) => !d.open && closeModal()}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Supprimer le dossier ?</DialogTitle>
              <DialogContent>
                « {modal.name} » sera supprimé. Les mails et sous-dossiers à l'intérieur seront
                masqués (récupération possible pendant 30 jours via la corbeille).
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeModal}>
                  Annuler
                </Button>
                <Button
                  appearance="primary"
                  disabled={deleteFolderMut.isPending}
                  onClick={submitDeleteFolder}
                >
                  {deleteFolderMut.isPending ? 'Suppression…' : 'Supprimer'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {modal?.kind === 'deleteMail' && (
        <Dialog open onOpenChange={(_e, d) => !d.open && closeModal()}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Supprimer le mail ?</DialogTitle>
              <DialogContent>
                « {modal.label} » sera supprimé (récupération possible pendant 30 jours via la
                corbeille).
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeModal}>
                  Annuler
                </Button>
                <Button
                  appearance="primary"
                  disabled={deleteMailMut.isPending}
                  onClick={submitDeleteMail}
                >
                  {deleteMailMut.isPending ? 'Suppression…' : 'Supprimer'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );

  if (allFlat.length === 0) {
    // État vide REPRIS : c'était une ligne de texte grise collée en haut à gauche,
    // qui ressemblait à un message d'erreur et laissait le reste du volet
    // désespérément blanc. Bloc centré, terme recherché rappelé (on sait ce qui
    // n'a rien donné), et surtout la SORTIE offerte sous forme de bouton — la
    // touche Échap était le seul recours annoncé, ce qui n'aide personne à la
    // souris.
    return (
      <>
        {scopeHint}
        <div className={styles.emptyState}>
          <SearchRegular className={styles.emptyIcon} />
          {/* Sans mot-clé, « Aucun résultat pour «  » » afficherait des guillemets
              vides : c'est alors le nombre de filtres qui explique ce qu'on
              cherchait. */}
          <span className={styles.emptyTitle}>
            {query.trim()
              ? `Aucun résultat pour « ${query.trim()} »`
              : 'Aucun mail ne correspond à ces filtres'}
          </span>
          <span className={styles.emptyHint}>
            {filtersActive
              ? // Un filtre est la cause la plus probable d'un résultat vide, et la
                // plus facile à corriger — d'autant qu'il peut être invisible,
                // replié dans son panneau.
                `Assouplissez les filtres (${activeFilterCount}) depuis le bouton en haut à droite.`
              : effectiveScope === 'all'
                ? "Vérifiez l'orthographe ou essayez un terme plus court."
                : // Portée restreinte : c'est la cause la plus probable, et c'est
                  // aussi la seule que l'utilisateur peut corriger d'un geste.
                  `La recherche ne couvre que ${scopeName ?? SCOPE_HINT[effectiveScope]}. Élargissez-la à « Partout » avec le filtre en haut à droite, ou essayez un autre terme.`}
          </span>
          {/* La sortie doit effacer TOUT ce qui restreint : effacer le seul terme
              en laissant des filtres actifs laisserait l'utilisateur devant le même
              vide, sans comprendre pourquoi le bouton n'a rien changé. */}
          <Button
            appearance="primary"
            size="small"
            onClick={() => {
              setSearchQuery('');
              if (filtersActive) clearSearchFilters();
            }}
          >
            {filtersActive ? 'Effacer la recherche et les filtres' : 'Effacer la recherche'}
          </Button>
          <span className={styles.emptyKeyHint}>ou appuyez sur Échap</span>
        </div>
        {dialogs}
      </>
    );
  }

  const projectHits = flat.filter((f) => f.kind === 'container' && f.hit.kind === 'project');
  const teamHits = flat.filter((f) => f.kind === 'container' && f.hit.kind === 'team_space');
  const folderHits = flat.filter((f) => f.kind === 'folder');
  const mailHits = flat.filter((f) => f.kind === 'mail');
  const focusedHit = flat[focusedIdx];

  const renderRow = (f: FlatHit, icon: React.ReactNode) => {
    const isContainer = f.kind === 'container';
    const container: Container | undefined = isContainer ? containerById.get(f.hit.id) : undefined;
    const isFavorite = isContainer ? favIds.has(f.hit.id) : undefined;

    // La NATURE du résultat vient de `f.kind` (dérivé de la section renvoyée par
    // l'API), pas de `hit.kind` : sur un hit dossier ou mail, `hit.kind` porte le
    // type du CONTENEUR (`project` / `team_space`).
    const menuContent =
      f.kind === 'container'
        ? containerMenu(f.hit, container, Boolean(isFavorite))
        : f.kind === 'folder'
          ? folderMenu(f.hit)
          : mailMenu(f.hit);

    return (
      <HitRow
        key={f.hit.id}
        hit={f.hit}
        icon={icon}
        query={query}
        subtitle={buildSubtitle(f)}
        focused={focusedHit?.hit.id === f.hit.id}
        scope={scope}
        selRef={refOfHit(f)}
        onFocusRow={() => setFocusedIdx(indexById.get(f.hit.id) ?? 0)}
        onOpen={f.activate}
        isFavorite={isFavorite}
        isRestricted={container?.isRestricted}
        menuContent={menuContent}
      />
    );
  };

  return (
    <>
      {scopeHint}
      {filterBar}
      {flat.length === 0 ? (
        // Cas différent du précédent : la recherche A trouvé des choses, mais pas
        // dans l'onglet sélectionné. Le dire, chiffrer ce qui existe ailleurs, et
        // offrir le retour à « Tous » — sinon l'utilisateur reste devant un vide
        // alors que ses résultats sont à un clic.
        <div className={styles.emptyState}>
          {/* La MÊME loupe que l'état « aucun résultat » juste au-dessus. L'entonnoir
              qu'elle remplace désignait le bouton de FILTRES, en haut à droite —
              alors que ce vide-ci ne vient pas des filtres mais de l'onglet
              sélectionné, et que le geste proposé est le bouton d'à côté. */}
          <SearchRegular className={styles.emptyIcon} />
          <span className={styles.emptyTitle}>Rien dans cette catégorie</span>
          <span className={styles.emptyHint}>
            {allFlat.length} résultat{allFlat.length > 1 ? 's' : ''}
            {query.trim() ? ` pour « ${query.trim()} »` : ''} dans les autres catégories.
          </span>
          <Button appearance="primary" size="small" onClick={() => setFilter('all')}>
            Voir tous les résultats
          </Button>
        </div>
      ) : (
        <Accordion
          multiple
          defaultOpenItems={['projects', 'teams', 'folders', 'mails']}
          className={styles.section}
        >
          {(filter === 'all' || filter === 'project') && projectHits.length > 0 && (
            <Section
              value="projects"
              label="Projets"
              icon={<Briefcase20Regular />}
              items={projectHits}
              labelClassName={styles.sectionLabel}
              renderRow={renderRow}
            />
          )}
          {(filter === 'all' || filter === 'team_space') && teamHits.length > 0 && (
            <Section
              value="teams"
              label="Espaces de service"
              icon={<People20Regular />}
              items={teamHits}
              labelClassName={styles.sectionLabel}
              renderRow={renderRow}
            />
          )}
          {(filter === 'all' || filter === 'folder') && folderHits.length > 0 && (
            <Section
              value="folders"
              label="Dossiers"
              icon={<FolderItemIcon />}
              items={folderHits}
              labelClassName={styles.sectionLabel}
              renderRow={renderRow}
            />
          )}
          {(filter === 'all' || filter === 'mail') && mailHits.length > 0 && (
            <Section
              value="mails"
              label="Mails"
              icon={<MailItemIcon />}
              items={mailHits}
              labelClassName={styles.sectionLabel}
              renderRow={renderRow}
            />
          )}
        </Accordion>
      )}
      {dialogs}
    </>
  );
}

/**
 * Une section pliable de résultats (Projets, Espaces de service, Dossiers, Mails).
 *
 * DÉFINIE AU NIVEAU DU MODULE, et c'est structurel — pas un rangement cosmétique.
 * Elle vivait dans le corps de `SearchResults` : son type était donc RECRÉÉ à
 * chaque rendu et React, voyant un type d'élément différent, démontait puis
 * remontait tout le sous-arbre de la section. Les états portés par les lignes en
 * dessous — survol, menu ⋯ ouvert, position d'ancrage du menu — repartaient de
 * zéro au moindre rendu du parent, si bien qu'un menu contextuel ouvert dans les
 * résultats pouvait se refermer seul. Ici son identité est figée, donc le
 * sous-arbre persiste.
 *
 * `labelClassName` et `renderRow` sont ce qu'elle capturait par fermeture ; ils
 * passent désormais explicitement par les props.
 */
function Section({
  value,
  label,
  icon,
  items,
  labelClassName,
  renderRow,
}: {
  value: string;
  label: string;
  icon: React.ReactNode;
  items: FlatHit[];
  labelClassName: string;
  renderRow: (f: FlatHit, icon: React.ReactNode) => ReactNode;
}) {
  return (
    <AccordionItem value={value}>
      <AccordionHeader expandIconPosition="end">
        <span className={labelClassName}>{label}</span>
        <CountBadge count={items.length} />
      </AccordionHeader>
      <AccordionPanel>{items.map((f) => renderRow(f, icon))}</AccordionPanel>
    </AccordionItem>
  );
}

interface HitRowProps {
  hit: SearchHit;
  icon: React.ReactNode;
  // Current query, used to highlight the matched fragments in the title.
  query: string;
  // Greyed "path" line under the title (project · parent-folder · date). Empty
  // string = no subtitle.
  subtitle: string;
  /** Curseur CLAVIER — la ligne d'où part Entrée. Distinct de « sélectionné ». */
  focused: boolean;
  /** Portée de multi-sélection partagée par toutes les lignes de la liste. */
  scope: string;
  /** Ce que cette ligne représente pour la sélection, ou `null` (cf. refOfHit). */
  selRef: SelectedRef | null;
  /** Amène le curseur clavier sur cette ligne (tout clic, y compris le droit). */
  onFocusRow: () => void;
  /** Ouvre le résultat. Au DOUBLE-clic — le clic simple ne fait que sélectionner. */
  onOpen: () => void;
  // Shown as a filled star on the right (container favorites).
  isFavorite?: boolean;
  // Espace à visibilité restreinte → petit cadenas à droite. `undefined` sur les
  // hits dossier/mail (la restriction est portée par l'espace, pas par la ligne).
  isRestricted?: boolean;
  /**
   * Contenu du menu contextuel (clic droit et bouton ⋯). `null` = pas de menu.
   *
   * UN NŒUD, PAS UNE LISTE D'ACTIONS. Le contrat précédent était un tableau de
   * `{icon, label, onClick}` : incapable d'exprimer un séparateur, un sous-menu ou
   * une entrée désactivée avec infobulle. Or les trois sont indispensables pour
   * offrir ici les MÊMES actions que dans l'arbre — `ItemActionMenuItems` émet
   * trois séparateurs et un « Coller » désactivé, et un espace porte la cascade
   * « Espaces collaboratifs ». Avec un nœud, ce composant partagé est réutilisé
   * littéralement, donc l'ordre et les libellés ne peuvent plus dériver.
   */
  menuContent?: ReactNode;
}

function HitRow({
  hit,
  icon,
  query,
  subtitle,
  focused,
  scope,
  selRef,
  onFocusRow,
  onOpen,
  isFavorite,
  isRestricted,
  menuContent,
}: HitRowProps) {
  const styles = useStyles();
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTarget, setMenuTarget] = useState<CursorTarget | null>(null);

  // La sélection est lue ET écrite PAR LIGNE, comme dans `MailRow` : le hook ne
  // fait que s'abonner au store, et c'est la ligne qui sait ce qu'elle
  // représente. Le menu de LOT est instancié ici pour la même raison — son
  // `onDone` doit refermer CE menu-ci.
  const sel = useRowSelection(scope);
  const selected = selRef ? sel.isSelected(selRef.id) : false;
  const multi = useMultiSelectionMenu({ items: sel.items, onDone: () => setMenuOpen(false) });

  // Le menu de lot REMPLACE celui de la ligne dès qu'elle fait partie d'une
  // sélection multiple : « Renommer », « Ouvrir », « Gérer la visibilité »
  // n'agiraient que sur une des lignes désignées, ce que l'utilisateur ne
  // verrait pas.
  const asBatch = multi.isMulti && selected;
  const hasMenu = Boolean(menuContent) || asBatch;

  /**
   * Clic simple : SÉLECTIONNER, jamais ouvrir.
   *
   * `sel.onRowClick` traite d'abord CTRL (ajoute/retire) et MAJ (étend depuis
   * l'ancre) et rend vrai quand il a consommé le geste. Un clic nu retombe sur
   * une sélection unique.
   *
   * Sur un ESPACE (`selRef` à `null`, cf. refOfHit) il n'y a rien à
   * sélectionner : un clic nu ABANDONNE la sélection en cours, comme un clic
   * dans le vide, et CTRL/MAJ ne font rien plutôt que de la détruire au moment
   * précis où l'utilisateur cherchait à l'étendre.
   */
  const handleClick = (e: React.MouseEvent) => {
    onFocusRow();
    if (!selRef) {
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (sel.items.length > 0) sel.clear();
      return;
    }
    if (sel.onRowClick(e, selRef)) return;
    sel.selectOnly(selRef);
  };

  return (
    <>
      <div
        className={mergeClasses(styles.hit, selected && styles.selected, focused && styles.focused)}
        onClick={handleClick}
        onDoubleClick={onOpen}
        title={hit.label}
        aria-selected={selected}
        {...(selRef ? sel.attrs(selRef) : {})}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onContextMenu={
          hasMenu
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onFocusRow();
                // Un clic droit désigne la ligne, comme dans l'Explorateur — mais
                // il ne CASSE pas une multi-sélection dont elle fait déjà partie :
                // ce serait ramener quatre éléments visés à un seul, juste avant
                // d'ouvrir le menu qui agit dessus.
                if (selRef) {
                  if (!selected) sel.selectOnly(selRef);
                } else if (sel.items.length > 0) {
                  sel.clear();
                }
                setMenuTarget(pointTarget(e.clientX, e.clientY));
                setMenuOpen(true);
              }
            : undefined
        }
      >
        {/* `=== false` strictement : `undefined` veut dire « le back-end ne le dit
            pas » (espaces, dossiers, ou API antérieure à la migration 0027), et non
            « non lu ». */}
        <UnreadBadge unread={hit.isRead === false}>{icon}</UnreadBadge>
        <div className={styles.hitBody}>
          <div
            className={
              hit.isRead === false ? mergeClasses(styles.label, styles.unreadLabel) : styles.label
            }
          >
            <HighlightText text={hit.label} query={query} />
          </div>
          {subtitle && <div className={styles.snippet}>{subtitle}</div>}
        </div>
        {isRestricted && (
          <LockClosed16Regular
            className={styles.restrictedIcon}
            title="Accès limité : seules les personnes et les groupes autorisés voient cet espace."
          />
        )}
        {isFavorite && <Star16Filled className={styles.favStar} title="Favori" />}
        {hasMenu && (
          /* CE WRAPPER N'EST PAS DÉCORATIF. Fluent monte le MenuPopover dans un
             PORTAIL, mais React fait remonter les événements du portail dans l'arbre
             REACT, pas dans l'arbre DOM : un clic sur une entrée atteint donc le
             `onClick` de la LIGNE, qui ouvre un onglet (espace, dossier) ou télécharge
             le .msg (mail). Le `stopPropagation` posé sur chaque entrée suffisait
             tant que le menu était une liste plate écrite ici ; il ne peut plus rien
             pour les entrées venues de `ItemActionMenuItems` ni pour le sous-menu.
             Même remède que `ContainerSection`, `FolderRow` et `MailRow`.
             Corollaire : les dialogues sont montés au niveau de la LISTE, hors de ce
             sous-arbre où les clics sont stoppés. */
          <div
            className={styles.menuWrap}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Menu
              open={menuOpen}
              onOpenChange={(_e, d) => {
                setMenuOpen(d.open);
                // Cf. FolderRow : l'ancre curseur ne doit pas survivre au menu.
                if (!d.open) setMenuTarget(null);
              }}
              positioning={menuPositioning(menuTarget)}
            >
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<MoreHorizontalRegular />}
                  className={hover || menuOpen ? styles.hoverShown : styles.hoverOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuTarget(null);
                    setMenuOpen(true);
                  }}
                />
              </MenuTrigger>
              <MenuPopover>
                {/* Monté SEULEMENT à l'ouverture : le contenu peut appeler des hooks
                    (le sous-menu collaboratif interroge Graph) et une recherche
                    affiche des dizaines de lignes. */}
                <MenuList>{menuOpen ? (asBatch ? multi.items : menuContent) : null}</MenuList>
              </MenuPopover>
            </Menu>
          </div>
        )}
      </div>
      {/* Dialogues du menu de LOT (« Déplacer vers… », confirmation de
          suppression). HORS du `MenuList`, qui est démonté avec le menu dès le
          premier clic — un dialogue posé dedans ne s'afficherait jamais. Ils sont
          `null` tant que la sélection ne compte pas plusieurs éléments. */}
      {multi.dialogs}
    </>
  );
}
